import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, GitCompareArrows, Radar } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BUCKETS = [
  { key: "feature_requests", label: "Feature Requests" },
  { key: "likes", label: "Likes" },
  { key: "dislikes", label: "Dislikes" },
];

function Column({ scan, bucket }) {
  if (!scan) {
    return <div className="flex-1 border border-dashed border-zinc-300 p-10 text-center text-zinc-400 font-mono text-sm">Select a scan…</div>;
  }
  const items = (scan.report?.[bucket] || []).slice(0, 10);
  return (
    <div className="flex-1 min-w-0" data-testid={`compare-col-${scan.id}`}>
      <div className="border border-zinc-200 bg-zinc-50 p-4 mb-3">
        <div className="font-display font-bold text-lg capitalize truncate">{scan.category}</div>
        <div className="font-mono text-xs text-zinc-500">{scan.total_reviews_analyzed} reviews · {scan.app_count} apps</div>
      </div>
      <div className="border border-zinc-200">
        {items.length === 0 && <div className="p-6 text-center text-zinc-400 text-sm font-mono">No data</div>}
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-3 p-3 border-b border-zinc-200 last:border-b-0">
            <div className={`rounded-full flex items-center justify-center font-mono font-bold h-9 w-9 flex-shrink-0 text-xs border ${i === 0 ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-zinc-100 text-zinc-900 border-zinc-200"}`}>
              {it.percentage}
            </div>
            <span className="text-sm text-zinc-800 leading-snug">{it.theme}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ComparePage() {
  const [scans, setScans] = useState([]);
  const [left, setLeft] = useState("");
  const [right, setRight] = useState("");
  const [bucket, setBucket] = useState("feature_requests");
  const [data, setData] = useState({});

  useEffect(() => {
    axios.get(`${API}/scans`).then(({ data }) => {
      const done = data.filter((s) => s.status === "completed");
      setScans(done);
      if (done[0]) setLeft(done[0].id);
      if (done[1]) setRight(done[1].id);
    });
  }, []);

  useEffect(() => {
    [left, right].forEach((id) => {
      if (id && !data[id]) {
        axios.get(`${API}/scans/${id}`).then(({ data: d }) => setData((p) => ({ ...p, [id]: d })));
      }
    });
  }, [left, right, data]);

  const leftScan = data[left];
  const rightScan = data[right];
  const options = useMemo(() => scans, [scans]);

  return (
    <div className="min-h-screen bg-white">
      <div className="border-b border-zinc-200 px-8 py-4 flex items-center gap-4 sticky top-0 bg-white/80 backdrop-blur-xl z-10">
        <Link to="/" className="flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900" data-testid="compare-back">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
        <div className="h-4 w-px bg-zinc-200" />
        <div className="flex items-center gap-2">
          <GitCompareArrows className="h-4 w-4 text-[#002FA7]" />
          <span className="font-display font-bold tracking-tight">Compare Categories</span>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-8">
        {scans.length < 2 ? (
          <div className="text-center py-24 text-zinc-400">
            <Radar className="h-8 w-8 mx-auto mb-3" />
            Run at least two scans to compare categories side by side.
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-4 mb-6">
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-1.5">Category A</label>
                <Select value={left} onValueChange={setLeft}>
                  <SelectTrigger className="w-[220px] rounded-sm border-zinc-300 h-11" data-testid="compare-left-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{options.map((s) => <SelectItem key={s.id} value={s.id}>{s.category}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-1.5">Category B</label>
                <Select value={right} onValueChange={setRight}>
                  <SelectTrigger className="w-[220px] rounded-sm border-zinc-300 h-11" data-testid="compare-right-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{options.map((s) => <SelectItem key={s.id} value={s.id}>{s.category}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Tabs value={bucket} onValueChange={setBucket} className="ml-auto">
                <TabsList className="bg-zinc-100 p-1 rounded-sm">
                  {BUCKETS.map((b) => (
                    <TabsTrigger key={b.key} value={b.key} className="rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs px-3" data-testid={`compare-bucket-${b.key}`}>
                      {b.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>
            <div className="flex flex-col md:flex-row gap-6">
              <Column scan={leftScan} bucket={bucket} />
              <Column scan={rightScan} bucket={bucket} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
